/* eslint-disable no-underscore-dangle */
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles'
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import classNames from 'classnames';

// FlyToInterpolator needed if using transitions
import MapGL, { Marker, Popup, NavigationControl } from 'react-map-gl';

// imported in <head>
// import mapStyleSheet from 'mapbox-gl/dist/mapbox-gl.css';
// import 'mapbox-gl/dist/mapbox-gl.css';

import Default, { Mobile } from '../../utils/mediaQueries';
import { withRtl } from '../hoc';
import style from './Mapbox.scss';

import SchoolPin from './SchoolPin';
import SchoolInfo from './SchoolInfo';
import ControlPanel from './ControlPanel';
import HomeIcon from '../icons/Home';
import MapLegend from './MapLegend';

class Mapbox extends Component {
  static propTypes = {
    home: PropTypes.oneOfType([
      PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      PropTypes.bool,
    ]),
    markers: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
    ),
    user: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
    isLoggedOutUser: PropTypes.bool,
    isRtl: PropTypes.bool,
    mobile: PropTypes.bool,
    popupClickHandlers: PropTypes.shape({
      onSchoolNameClick: PropTypes.func,
      onProgramClick: PropTypes.func,
    }),
    currentSchoolDetail: PropTypes.number,
    admissionProcess: PropTypes.string,
    isSchoolDetail: PropTypes.bool,
    page: PropTypes.number,
    getBoundingSchoolPins: PropTypes.func.isRequired,
  };

  static defaultProps = {
    home: false,
    markers: [
      {
        id: 10,
        lat: 40.7331939,
        lng: -73.993982,
        icon: 'default',
        distance: '',
      },
    ],
    user: false,
    isLoggedOutUser: false,
    isRtl: false,
    mobile: false,
    popupClickHandlers: {
      onSchoolNameClick: () => {},
      onProgramClick: () => {},
    },
    currentSchoolDetail: null,
    admissionProcess: null,
    isSchoolDetail: false,
    page: null,
  };

  constructor(props) {
    super(props);
    this.mapRef = createRef();
    const homeLL = this.getHomeLL();
    this.initialState = {
      viewport: {
        width: '100%',
        height: '100vh',
        latitude: homeLL ? homeLL.lat : 40.7331939,
        longitude: homeLL ? homeLL.lng : -73.993982,
        zoom: 10,
        bearing: 0,
        pitch: 0
      },
      popupInfo: null,
      justSetAddress: false,
      firstRender: true,
    };

    this.mapBounds = {};

    this.state = this.initialState;
  }

  componentDidMount() {
    // need to calculate the bounds based on the markers
    // don't like doing this here
    // but the DOM element isn't available
    // until mount for size
    window.addEventListener('resize', this.updateMapDimensions);
    // this.fitBounds(); this is breaking right to left languages. so commented this code.

    setTimeout(() => {
      const map = this.mapRef.current?.getMap();
      if (map) {
        map.scrollZoom.enable();
        map.dragPan.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();

        /**
         * When mapbox displays RTL languages,
         * prevent from setting a height of 0 on map:
         */
        if (map && map._containerHeight === 0) {
          map._containerHeight = window.innerHeight;
        }

      }
    }, 500);
  }

  componentDidUpdate(prevProps) {
    // sk: this might need more based on same function in original Maps
    const { markers, currentSchoolDetail, user, page } = this.props;
    const { firstRender, popupInfo, justSetAddress } = this.state;
    // close popup if one has been opened and we don't have any more markers
    if (!markers.length && popupInfo) {
      // we will get another transition so there's a check in this.popup()
      // which is called afte transition
      this.handlePopupClose();
    }

    if (prevProps.user.set !== user.set && user.set) {
      this.setState({ ...this.state, justSetAddress: true });
    }
    // Pagination changes `page` one render before the new page's markers arrive (async
    // fetch), so latch it and re-fit once those markers load. Panning the map changes
    // markers but not `page`, so this won't fight manual panning, and the programmatic
    // fitBounds fires a moveend with no originalEvent (getMapBounds ignores it) so it
    // won't trigger a bounds refetch.
    if (prevProps.page !== page) {
      this.refitOnNextMarkers = true;
    }
    // new markers, new bounds
    if (!_.isEqual(_.sortBy(prevProps.markers), _.sortBy(markers)) && markers.length) {
      if (justSetAddress || firstRender || this.refitOnNextMarkers) {
        this.refitOnNextMarkers = false;
        this.fitBounds();
      }
    }

    if (currentSchoolDetail) {
      const markerIndex = markers.findIndex(marker => marker.id === currentSchoolDetail);
      if (markerIndex !== -1) {
        this.centerOnPopUp(markerIndex);
      }
    }
    // loop through all markers (schools) and check to see if we should handlePopupTransition
    if (currentSchoolDetail && prevProps.currentSchoolDetail !== currentSchoolDetail) {
      this.reduceMarkers(markers).forEach(school => {
        school.popups.forEach(popup => {
          if (popup.id === currentSchoolDetail) {
            this.popup({ school });
          }
        });
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateMapDimensions);
  }

  handleMapLoad = () => {
    const map = this.mapRef.current?.getMap();
    if (map) {
      // On desktop, enable cooperative gestures so a trackpad pinch (which the browser
      // reports as ctrl+wheel) zooms the map instead of scaling the whole page, while a
      // plain scroll still scrolls the page (important on the scrollable detail page).
      // Disabled on mobile so one-finger touch panning keeps working. cooperativeGestures
      // is a constructor option that reuseMaps does not re-apply on reuse, so set it
      // imperatively here; the scrollZoom disable/enable re-runs the blocker setup so the
      // "use ctrl + scroll" overlay matches the current mode.
      // TODO(i18n): the cooperative-gestures overlay text is English-only. Localize via
      // mapbox's `locale` option (keys ScrollZoomBlocker.CtrlMessage / .CmdMessage) using
      // injectIntl, for the cs-CZ and ar locales.
      const cooperative = !this.props.mobile;
      if (map.getCooperativeGestures() !== cooperative) {
        map.scrollZoom.disable();
        map.setCooperativeGestures(cooperative);
        map.scrollZoom.enable();
      }
    }

    // componentDidUpdate only calls fitBounds when the markers prop *changes*.
    // When the map mounts after markers are already loaded — e.g. the mobile map
    // is lazy-mounted only once the user opens it — there is no subsequent marker
    // change to center on, so fit here on load instead. (load fires on reuse too.)
    if (this.state.firstRender && this.props.markers.length) {
      this.fitBounds();
    }
  };

  onViewportChange = viewport => {
    this.setState({
      ...this.state,
      viewport: {
        ...this.state.viewport,
        ...viewport,
      },
    });
  }

  getMapBounds = evt => {
    const { firstRender, justSetAddress } = this.state;
    if (firstRender) {
      this.setState({ ...this.state, firstRender: false });
    }
    if (justSetAddress) {
      this.setState({ ...this.state, justSetAddress: false });
    }

    if (!evt?.originalEvent) return;

    const type = evt.originalEvent.type;
    // Ignore scroll-like interactions
    if (this.props.mobile && ['wheel', 'resize', 'touchmove'].includes(type)) {
      return;
    }

    if (!evt || evt.distance !== 0) {
      if (!this.mapRef.current) return;
      const mapGL = this.mapRef.current.getMap();
      const bounds = mapGL.getBounds();
      const neCoords = [bounds._ne.lat, bounds._ne.lng];
      const swCoords = [bounds._sw.lat, bounds._sw.lng];
      if (this.props.getBoundingSchoolPins) {
        this.props.getBoundingSchoolPins(neCoords, swCoords);
      }
    }
  };

  getMinOrMax = (minOrMax, latOrLng) => {
    const { markers } = this.props;
    // add in the home if it's there
    const cloned = _.clone(markers);
    const homeLL = this.getHomeLL();
    if (homeLL) {
      cloned.push(homeLL);
    }
    return minOrMax === 'max'
      ? _.maxBy(cloned, value => value[latOrLng])[latOrLng]
      : _.minBy(cloned, value => value[latOrLng])[latOrLng];
  };

  getBounds = () => {
    const maxLat = this.getMinOrMax('max', 'lat');
    const minLat = this.getMinOrMax('min', 'lat');
    const maxLng = this.getMinOrMax('max', 'lng');
    const minLng = this.getMinOrMax('min', 'lng');

    const southWest = [minLng, minLat];
    const northEast = [maxLng, maxLat];

    return [southWest, northEast];
  };

  getStudentHomeAddress = () => {
    const { user, isLoggedOutUser } = this.props;
    if (isLoggedOutUser) {
      return user.formattedAddress;
    }

    return user.getStudentHomeAddress();
  };

  getHomeLL = () => {
    const { user, home, isLoggedOutUser } = this.props;
    if (!isLoggedOutUser) {
      let latitude = home ? home.lat : null;
      let longitude = home ? home.lng : null;
      if (!home && user) {
        const address = user.getStudentAddress();
        latitude = address.latitude;
        longitude = address.longitude;
      }

      if (latitude && longitude) {
        return {
          lat: latitude,
          lng: longitude,
        };
      }
    } else if (user.set && isLoggedOutUser) {
      const { lat, lng } = user.latlng;
      return {
        lat,
        lng,
      };
    }

    return null;
  };

  fitBounds = () => {
    const { markers } = this.props;
    // needed for SchoolDetails ... markers aren't initially set?
    if (!markers.length) {
      return;
    }

    // MapGL runs in uncontrolled mode (initialViewState), so updating
    // this.state.viewport no longer moves the map. We must drive the camera
    // through the native mapbox-gl instance, the same way handleHomeIconClick does.
    const map = this.mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const bounds = this.getBounds();
    // add in the home if it's there
    const cloned = _.clone(markers);
    const homeLL = this.getHomeLL();
    if (homeLL) {
      cloned.push(homeLL);
    }

    // fitBounds breaks if we have the same lng/lat for each bound,
    // which means we only have one marker
    if (cloned.length === 1) {
      map.flyTo({
        center: [markers[0].lng, markers[0].lat],
        essential: true,
      });

      return;
    }

    // maxZoom guards against degenerate/tightly-clustered bounds (e.g. multiple
    // programs at the same address) zooming all the way to street level. Spread-out
    // schools compute a zoom well below this, so they are unaffected.
    map.fitBounds(bounds, { padding: 50, offset: [0, -100], maxZoom: 15 });
  };

  reduceMarkers = markers => {
    const mergedMarkers = {};
    markers.forEach(marker => {
      const key = `${marker.lat}${marker.lng}`;
      if (mergedMarkers[key]) {
        if (marker.popup) {
          mergedMarkers[key].icon = this.iconPriority([mergedMarkers[key].icon, marker.icon]);
          mergedMarkers[key].popups.push(marker.popup);
        }
      } else {
        mergedMarkers[key] = marker;
        mergedMarkers[key].popups = marker.popup ? [marker.popup] : [];
      }
    });

    return Object.keys(mergedMarkers).map(key => mergedMarkers[key]);
  };

  iconPriority = icons => {
    const savedApplications = 'savedApplications';
    const savedPrograms = 'savedPrograms';
    const colorIconProgramsSaved = [
      'districtSchoolSaved',
      'earlyEdCenterSaved',
      'charterSchoolSaved',
      'preKCenterSaved',
      'halfDayOnlySaved',
      'earlyLearnSaved',
    ];
    const colorIconProgramsAdded = [
      'districtSchoolAdded',
      'earlyEdCenterAdded',
      'charterSchoolAdded',
      'preKCenterAdded',
      'halfDayOnlyAdded',
      'earlyLearnAdded',
    ];
    const uniqueIcons = [...new Set(icons)];

    const isPK = window.App.state.user.admissionProcess === 'PK' || this.props.admissionProcess === 'PK';
    const is2K = window.App.state.user.admissionProcess === '2K' || this.props.admissionProcess === '2K';
    const is3K = window.App.state.user.admissionProcess === '3K' || this.props.admissionProcess === '3K';

    if (isPK || is2K || is3K) {
      if (colorIconProgramsAdded.some(r => uniqueIcons.includes(r))) {
        return 'savedApplicationsOver';
      }
      if (colorIconProgramsSaved.some(r => uniqueIcons.includes(r))) {
        return 'savedProgramsOver';
      }

      return 'defaultEligibility';
    }

    if (uniqueIcons.length === 1) {
      return uniqueIcons[0];
    }

    if (uniqueIcons.indexOf(savedApplications) > -1) {
      return savedApplications;
    }
    if (uniqueIcons.indexOf(savedPrograms) > -1) {
      return savedPrograms;
    }

    return 'defaultEligibility';
  };

  updateMapDimensions = _.debounce(() => {
    if (this.props.mobile) {
      return;
    }
    this.fitBounds();
  }, 250);

  // Show the popup
  // we only want to do this if we have the school in our data
  // when paginating, and a popup is present,
  // it's told to close however we then get it back
  // because of the transition
  popup = ({ school }) => {
    const { markers, isSchoolDetail } = this.props;
    if (isSchoolDetail) {
      return;
    }

    markers.forEach(s => {
      if (s.id === school.id) {
        this.setState({
          ...this.state,
          popupInfo: school,
        });
      }
    });
  };

  handleHomeIconClick = () => {
    const { lat, lng } = this.getHomeLL();
    const map = this.mapRef.current?.getMap();
    if(map){
      map.flyTo({
        center: [lng, lat],
        essential: true
      })
    }
    this.onViewportChange({
      longitude: lng,
      latitude: lat,
    });
  };

  handlePopupClose = () => {
    this.setState({
      ...this.state,
      popupInfo: null,
    });
  };

  centerOnPopUp = markerIndex => {
    const { markers } = this.props;
    const { viewport } = this.state;

    if (markers[markerIndex].lat === viewport.latitude) {
      return;
    }
    this.setState({
      ...this.state,
      viewport: {
        ...this.state.viewport,
        latitude: markers[markerIndex].lat,
        longitude: markers[markerIndex].lng,
      },
    });
  };

  renderHomeMarker = () => {
    const homeLL = this.getHomeLL();
    if (homeLL) {
      const { lat, lng } = homeLL;
      return (
        <Marker longitude={lng} latitude={lat} anchor="bottom">
          <HomeIcon size={33} fill="#024cbc" />
        </Marker>
      );
    }

    return null;
  };

  renderSchoolMarkers = () => {
    const { markers } = this.props;
    return this.reduceMarkers(markers).map(school => (
      <Marker
        key={`school-marker-${school.id}`}
        longitude={school.lng}
        latitude={school.lat}
        anchor="bottom"
        // offsetTop={mobile ? 200 : 0}
      >
        <SchoolPin
          width={33}
          height={40}
          type={school.icon}
          onClick={() => this.popup({ school })}
          overwriteInnerShapeBy={
            school.popups && school.popups.length > 1 ? `number$$${school.popups.length}` : school.overwriteInnerShapeBy
          }
        />
      </Marker>
    ));
  };

  renderPopup = () => {
    const { popupInfo } = this.state;
    const { popupClickHandlers } = this.props;
    const { onSchoolNameClick, onProgramClick } = popupClickHandlers;
    return (
      popupInfo && (
        <Popup
          tipSize={10}
          anchor="top"
          offsetTop={-10}
          // is there a way to do this so it opens below center
          // 200 is offsetTop of the Marker
          // offsetTop={!mobile ? 200 - 45 : -45}
          longitude={popupInfo.lng}
          latitude={popupInfo.lat}
          // closeButton={false}
          closeOnClick={false}
          onClose={this.handlePopupClose}
          // If true, the anchor will be dynamically adjusted to ensure the popup falls within the map container. (eg when opening near the edge)
          dynamicPosition
        >
          <SchoolInfo
            info={popupInfo.popups}
            homeAddress={this.getStudentHomeAddress()}
            onSchoolTitleClick={onSchoolNameClick}
            onSchoolProgramClick={onProgramClick}
          />
        </Popup>
      )
    );
  };

  renderMapLegend = () => {
    const { markers, isRtl } = this.props;
    return (
      <div>
        <Default>
          <MapLegend isRtl={isRtl} markers={markers} />
        </Default>
        <Mobile>
          <MapLegend isRtl={isRtl} markers={markers} mobile />
        </Mobile>
      </div>
    );
  };

  render() {
    const { mobile, isRtl, isLoggedOutUser, user } = this.props;
    const hasHome = !isLoggedOutUser || (user.set && isLoggedOutUser);

    // determine position of the map controls
    let mapControlStyles = {'position': 'absolute', 'z-index': 1 };
    const mapControlClasses = [style.mapControls];
    let control_postion = 'top-right';
    if (mobile && isRtl) {
      mapControlClasses.push(style.bottomLeft);
      mapControlStyles = {...mapControlStyles, ...{'bottom': hasHome ?'120px' : '75px', 'left': '0'}};
      control_postion = 'bottom-left';
    } else if (mobile) {
      mapControlClasses.push(style.bottomRight);
      mapControlStyles = {...mapControlStyles, ...{'bottom': hasHome ?'120px' : '75px', 'right': '0'}};
      control_postion = 'bottom-right';
    } else if (isRtl) {
      mapControlClasses.push(style.topLeft);
      mapControlStyles = {...mapControlStyles, ...{'top': '160px', 'left': '10px'}};
      control_postion = 'top-left';
    } else {
      mapControlClasses.push(style.topRight);
      mapControlStyles = {...mapControlStyles, ...{'top': '55px', 'right': '10px'}};
      control_postion = 'top-right';
    }

    return (
      <div
        className={style.mapsWrapper}
        ref={el => {
          this.mapsWrapper = el;
        }}
      >
        <MapGL
          ref={this.mapRef}
          // reuseMaps recycles the underlying mapbox-gl instance across mount/unmount
          // cycles instead of destroying it. mapbox-gl v2+ bills per map load (new Map()),
          // so without this every route navigation (search <-> detail, etc.) created a
          // fresh billable load. onLoad re-fires and initialViewState is re-applied on
          // reuse, so existing behavior is preserved.
          // Desktop only: on mobile the map is lazy-mounted, so closing it recycles the
          // instance into react-map-gl's pool with its canvas detached from the DOM. Mobile
          // browsers reclaim the detached WebGL context, so reusing it on reopen renders a
          // broken map. A fresh new Map() each open gets a new context and works, and the
          // lazy-mount already prevents loads for users who never open the map.
          reuseMaps={!mobile}
          mapboxAccessToken={window.App.state.runtime.mapboxApiAccessToken || 'test'}
          mapStyle={window.App.state.runtime.mapboxApiStyleUrl}
          initialViewState={this.state.viewport}
          scrollZoom={true}
          cooperativeGestures={!mobile}
          dragPan={!mobile}
          doubleClickZoom={false}
          boxZoom={false}
          attributionControl={false}
          onMoveEnd={this.getMapBounds}
          touchZoomRotate={true}
          keyboard={false}
          onLoad={this.handleMapLoad}
        >
          <div className={classNames(...mapControlClasses)}>
            {hasHome && (
              <Default>
                <ControlPanel onClickHome={this.handleHomeIconClick} />
              </Default>
            )}
            <NavigationControl showCompass={false} position={control_postion} style={mapControlStyles} />
            {hasHome && (
              <Mobile>
                <ControlPanel onClickHome={this.handleHomeIconClick} />
              </Mobile>
            )}
          </div>
          {this.renderSchoolMarkers()}
          {this.renderHomeMarker()}
          {this.renderPopup()}
          {this.renderMapLegend()}
        </MapGL>
      </div>
    );
  }
}

const mapStateToProps = ({ runtime }) => ({ staticUrl: runtime.staticUrl });

export default compose(
  //
  // injectIntl,
  withRtl(style),
  withStyles(style),
  connect(mapStateToProps),
)(Mapbox);
